import {Component, OnInit, Renderer2} from "@angular/core";
import {ToastrService} from "ngx-toastr";
import {AppService} from "@services/app.service";
import {Router} from "@angular/router";
import {NgxSpinnerService} from "ngx-spinner";
import {ConfigService} from "@services/configService";
import {LoggingService} from "@services/logging.service";
import {UserContextService} from "@services/user-service/usercontext.service";
import {catchError, finalize, of, Subject, takeUntil} from "rxjs";
import {ShopOwnerDetails} from "@pages/modal/shop/shopOwnerDetails";
import {shopDetails} from "@pages/modal/shopdetails";
import {Shops} from "@pages/modal/shop/Shops";
import {ShopProductDetails} from "@pages/modal/shopProductDetails/ShopProductDetails";

@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss'],
})

export class UserProfileComponent implements OnInit {

    destroy$: Subject<boolean> = new Subject<boolean>();
    userAndShopDetailsById: ShopOwnerDetails | undefined;
    isCardCollapsed: boolean[] = [];
    shopId: number = 0;
    shopDetails: Shops[] = [];
    productDetails: ShopProductDetails[] = [];
    userId: number = 0;

    constructor(
        private renderer: Renderer2,
        private toastr: ToastrService,
        private appService: AppService,
        private router: Router,
        private spinner: NgxSpinnerService,
        private configService: ConfigService,
        private loggingService: LoggingService,
        private userContextService: UserContextService
    ) {

    }


    ngOnInit(): void {
        const userId = this.userContextService.getUserId();
        this.userId = userId;
        if (userId !== null) {
            this.getShopOwnerDetailById(userId);
        } else {
            this.router.navigate(['/main/user-list']);
        }
    }


    getShopOwnerDetailById(userId: number) {
        this.spinner.show();
        this.appService.getShopOwnerDetailById(userId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.spinner.hide()),
                catchError((error: any) => {
                    console.error('Error fetching shop & product detail:', error);
                    this.toastr.error('Failed to load shop & product detail. Please try again later.', 'Error');
                    return of(null); // Return a safe observable to prevent breaking the stream
                })
            )
            .subscribe((shopOwnerDetail) => {
                if (shopOwnerDetail) {
                    this.userAndShopDetailsById = shopOwnerDetail;
                    this.loggingService.log('shop & product detail::', this.userAndShopDetailsById);
                    this.shopDetails = this.userAndShopDetailsById.shopDetails;
                } else {
                    this.toastr.info('No shops found', 'Shops');
                }
            })
    }


    getShopBasedProductDetails(traderId: number) {
        this.spinner.show();
        this.appService.getShopProductDetails(traderId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.spinner.hide()),
                catchError((error: any) => {
                    console.error('Error fetching shop id based product detail:', error);
                    this.toastr.error('Failed to load shop product detail. Please try again later.', 'Error');
                    return of(null);
                })
            )
            .subscribe((shopProductDetails) => {
                if (shopProductDetails) {
                    this.productDetails = shopProductDetails;
                    this.productDetails.sort((a, b) => b.shopProductId - a.shopProductId);
                    this.loggingService.log('product detail::', this.productDetails);
                } else {
                    this.toastr.info('No products found', 'Products');
                }
            })
    }


    getApprovalStatusText(status: string): string {
        const statusMap: { [key: string]: string } = {
            'APPROVED': 'Approved',
            'NOT APPROVED': 'Not Approved',
            'RE APPROVED': 'Re Approved',
            'SUSPENDED': 'Suspended'
        };

        return statusMap[status] || 'Unknown Status';
    }


    initializeCardStates(): void {
        if (this.shopDetails && this.shopDetails.length > 0) {
            this.isCardCollapsed = Array(this.shopDetails.length).fill(true);
        }
    }

    // Toggles the collapse state of the shop card and loads products if expanded
    toggleCard(index: number, traderId: number): void {
        this.isCardCollapsed[index] = !this.isCardCollapsed[index];
        if (this.isCardCollapsed[index]) {
            this.getShopBasedProductDetails(traderId);
        }
    }


    getFormattedAddress(shop: any): string {
        if (!shop) return '';

        const addressParts = [
            shop.streetAddress,
            shop.area,
            shop.city,
            shop.state,
            shop.postalCode
        ];

        // Remove empty/null/undefined parts
        return addressParts.filter(part => !!part).join(', ');
    }


    getDeliveryLeadDate(deliveryLeadDate: string): string {
        const days = Number(deliveryLeadDate);

        if (isNaN(days) || days < 0 || days > 10) {
            return "Invalid lead time";
        }

        return days === 0 ? "Same day" : `${days} day${days > 1 ? 's' : ''}`;
    }


    //update shop owner status by userId
    approveStatus(status: string, supplierId: number) {
        const updateUserStatus = {
            userId: supplierId,
            approvalStatus: status
        }
        this.loggingService.log(`update shop owner status`, updateUserStatus);
        this.spinner.show();
        this.appService.updateShopOwnerStatusByUserId(updateUserStatus)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.spinner.hide()),
                catchError((error: any) => {
                    console.error('Error updating shop owner status:', error);
                    this.toastr.error('Failed to load update shop owner status. Please try again later.', 'Error');
                    return of(null); // Return a safe observable to prevent breaking the stream
                })
            )
            .subscribe((status) => {
                if (status?.statusCode === 200) {
                    this.toastr.success(`Shop owner status has been updated to ${updateUserStatus.approvalStatus}`, 'Status');
                    this.getShopOwnerDetailById(this.userId);
                } else {
                    this.toastr.info('Unable to update the shop owner status.', 'Status');
                }
            });
    }

    shopApproveStatus(status: string, shopId: number, shopOwnerStatus: string) {
        if (shopOwnerStatus !== 'APPROVED' && shopOwnerStatus !== 'RE APPROVED') {
            this.toastr.info('Shop owner status must be Approved or Re Approved proceed.', 'Status');
            return;
        }

        const updateShopStatus = {
            shopId: shopId,
            status: status
        }
        this.loggingService.log(`update shop status`, updateShopStatus);
        this.spinner.show();
        this.appService.updateShopStatusShopId(updateShopStatus)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.spinner.hide()),
                catchError((error: any) => {
                    console.error('Error updating shop status by shop id:', error);
                    this.toastr.error('Failed to load update shop status. Please try again later.', 'Error');
                    return of(null); // Return a safe observable to prevent breaking the stream
                })
            )
            .subscribe((status) => {
                if (status?.statusCode === 200) {
                    this.toastr.success(`Shop status has been updated to ${updateShopStatus.status}.`, 'Status');
                    this.getShopOwnerDetailById(this.userId);
                } else {
                    this.toastr.info('Unable to update the shop owner status.', 'Status');
                }
            });
    }

}